// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { BrandStateContext } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React, { FC, useContext, useMemo } from "react";

// <xng-change>
const enum fields {
    showShopAllProductsLink = "showShopAllProductsLink",
    showVisitBrandWebsiteLink = "showVisitBrandWebsiteLink",
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.showShopAllProductsLink]: boolean;
        [fields.showVisitBrandWebsiteLink]: boolean;
    };
}

type Props = OwnProps;
// </ xng-change>

export interface BrandDetailsImageStyles {
    container?: GridContainerProps;
    imageItem?: GridItemProps;
    image?: LazyImageProps;
    imageMissingText?: TypographyPresentationProps;
    linksItem?: GridItemProps;
    linksContainer?: GridContainerProps;
    productLinkItem?: GridItemProps;
    productLink?: LinkPresentationProps;
    externalLinkItem?: GridItemProps;
    externalLink?: LinkPresentationProps;
    // XNG-Changes
    fullWidthProductLinkItem?: GridItemProps;
    htmlItem?: GridItemProps;
    htmlContainer?: InjectableCss;
}

export const imageStyles: BrandDetailsImageStyles = {};

const styles = imageStyles;

const BrandDetailsImage: FC<Props> = ({ fields: { showShopAllProductsLink, showVisitBrandWebsiteLink } }) => {
    const { value: brand } = useContext(BrandStateContext);
    if (!brand) {
        return null;
    }

    // if there is a body tag, keep only what's between the body tag, then change those characters so the whole string is wrapped in a div tag instead
    let updatedHtmlContent = brand.htmlContent ? brand.htmlContent : "";
    const bodyTag = brand.htmlContent?.indexOf("<body");
    if (bodyTag > -1 && updatedHtmlContent) {
        updatedHtmlContent = `<div${brand.htmlContent.substring(
            bodyTag + 5,
            brand.htmlContent.indexOf("</body>"),
        )}</div>`;
    }

    const showExternalLink = useMemo(() => {
        return brand.externalUrl && showVisitBrandWebsiteLink;
    }, [brand.externalUrl, showVisitBrandWebsiteLink]);

    return (
        <GridContainer data-test-selector="brandImage" {...styles.container}>
            {/* xng-change */}
            {(showShopAllProductsLink || showExternalLink) && (
                <GridItem {...styles.linksItem}>
                    <GridContainer {...styles.linksContainer}>
                        {showShopAllProductsLink && (
                            <GridItem
                                {...(showExternalLink ? styles.productLinkItem : styles.fullWidthProductLinkItem)}
                            >
                                <Link
                                    data-test-selector="brandToProductPageLink"
                                    href={brand.productListPagePath}
                                    {...styles.productLink}
                                    aria-label="Shop All Products"
                                >
                                    {translate("Shop All Products")}
                                </Link>
                            </GridItem>
                        )}
                        {showExternalLink && (
                            <GridItem
                                {...(showShopAllProductsLink
                                    ? styles.externalLinkItem
                                    : styles.fullWidthProductLinkItem)}
                            >
                                <Link
                                    data-test-selector="brandExternalUrlLink"
                                    href={brand.externalUrl}
                                    {...styles.externalLink}
                                    aria-label="Visit brand Website"
                                >
                                    {translate("Visit Brand Website")}
                                </Link>
                            </GridItem>
                        )}
                    </GridContainer>
                </GridItem>
            )}
            {/* XNG-Changes: restructure image after links - following the VD */}
            <GridItem {...styles.imageItem}>
                {
                    brand.featuredImagePath ? (
                        <LazyImage
                            width="500"
                            height="300"
                            data-test-selector="brandFeaturedImage"
                            altText={brand.featuredImageAltText}
                            src={brand.featuredImagePath}
                            {...styles.image}
                        />
                    ) : null
                    //  (
                    // <Typography data-test-selector="brandFeaturedImageMissingText" {...styles.imageMissingText}>
                    //     {brand.name}
                    // </Typography>
                    // )
                }
            </GridItem>
            {/* XNG-Changes: add htmlcontent gridItem in this file instead of a separate widget to include it as a gridItem */}
            {brand.htmlContent && updatedHtmlContent && (
                <GridItem {...styles.htmlItem}>
                    <StyledWrapper {...styles.htmlContainer} data-test-selector="brandHTMLContent">
                        {parse(updatedHtmlContent, parserOptions)}
                    </StyledWrapper>
                </GridItem>
            )}
        </GridContainer>
    );
};

interface BrandDetailsLinkProps {
    path?: string;
    gridItemStyles?: GridItemProps;
    linkStyles?: LinkPresentationProps;
}

const BrandLink: FC<BrandDetailsLinkProps> = ({ path, gridItemStyles, linkStyles, children }) => {
    if (!path) {
        return null;
    }
    return (
        <GridItem {...gridItemStyles}>
            <Link
                data-test-selector="brandToProductPageLink"
                href={path}
                {...linkStyles}
                aria-label="brand To Product Page Link"
            >
                {children}
            </Link>
        </GridItem>
    );
};

const widgetModule: WidgetModule = {
    component: BrandDetailsImage,
    definition: {
        group: "Brand Details",
        icon: "image",
        displayName: "Brand Image",
        allowedContexts: ["BrandDetailsPage"],
        // xng-change
        fieldDefinitions: [
            {
                name: fields.showShopAllProductsLink,
                displayName: "Show Shop All Products Link",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.showVisitBrandWebsiteLink,
                displayName: "Show Visit Brand Website Link",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
